import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Alert, Input } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * OVO-126 — confirmation modal for deleting a route, with the option to
 * also delete the route's orders (instead of detaching them back to
 * UNASSIGNED, where they'd be re-imported on the next Optimize).
 *
 * Used by both the Routes list page (per-row + bulk) and the Route detail
 * page. `onConfirm` receives the checkbox value: onConfirm(deleteOrders).
 */
const DeleteRouteModal = ({
  open,
  onCancel,
  onConfirm,
  count = 1,
  routeId,
  dispatched = false,
  confirmLoading = false,
  // Force-delete (Felipe, Aug 2026): ids of selected routes that are
  // in-progress/completed. Deleting those is PERMANENT (no trash) and is
  // gated behind typing the ids — the GitHub "type the name" pattern, since a
  // completed route is billing history and a checkbox is too easy to tick.
  forceRouteIds = [],
}) => {
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');
  const [deleteOrders, setDeleteOrders] = useState(false);
  const [forceText, setForceText] = useState('');

  // Reset each time the modal (re)opens so it never carries a stale
  // "delete orders" choice or a typed confirmation into the next deletion.
  useEffect(() => {
    if (open) {
      setDeleteOrders(false);
      setForceText('');
    }
  }, [open]);

  const needsForce = forceRouteIds.length > 0;
  const expectedForceText = forceRouteIds.join(' ');
  const forceConfirmed = needsForce && forceText.trim() === expectedForceText;
  const normalCount = count - forceRouteIds.length;
  // Nothing deletable without the typed confirmation → keep OK disabled.
  const okDisabled = needsForce && normalCount === 0 && !forceConfirmed;

  const isBulk = count > 1;
  const heading = isBulk
    ? t('deleteRoutesConfirm', { count })
    : routeId
      ? t('deleteRouteConfirm', { id: routeId })
      : t('deleteRoute');

  return (
    <Modal
      open={open}
      title={t('deleteRoute')}
      onCancel={onCancel}
      onOk={() => onConfirm(deleteOrders, forceConfirmed)}
      okText={tc('delete')}
      cancelText={tc('cancel')}
      okButtonProps={{ danger: true, loading: confirmLoading, disabled: okDisabled }}
      // Sit above the fixed navbar (999), drawers (1010), popovers (1030) and
      // toasts (1050) — the default modal z-index (1000) let those cover it.
      zIndex={1060}
      destroyOnClose
    >
      <p>{heading}</p>
      {dispatched && (
        <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={t('deleteRouteDispatchedHint')} />
      )}
      <Checkbox checked={deleteOrders} onChange={(e) => setDeleteOrders(e.target.checked)}>
        {t('deleteOrdersToo')}
      </Checkbox>
      <Alert
        style={{ marginTop: 12 }}
        type={deleteOrders ? 'warning' : 'info'}
        showIcon
        message={deleteOrders ? t('deleteRouteDeleteOrdersHint') : t('deleteRouteKeepOrdersHint')}
      />
      {needsForce && (
        <>
          <Alert
            style={{ marginTop: 12 }}
            type="error"
            showIcon
            message={t('forceDeleteBlocked', { count: forceRouteIds.length })}
            description={t('forceDeleteBlockedDesc')}
          />
          <p style={{ margin: '12px 0 4px' }}>
            {t('forceDeleteType')} <code>{expectedForceText}</code>
          </p>
          <Input
            value={forceText}
            onChange={(e) => setForceText(e.target.value)}
            placeholder={expectedForceText}
            status={forceText && !forceConfirmed ? 'error' : undefined}
          />
          {normalCount > 0 && !forceConfirmed && (
            <p style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12 }}>{t('forceDeleteSkipNote')}</p>
          )}
        </>
      )}
    </Modal>
  );
};

export default DeleteRouteModal;
